import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { useNavigate } from "react-router-dom";
const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const [dialogData, setDialogData] = useState(null);
  const [uncompletedGameWS, setUncompletedGameWS] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showHistoryLimitDialog, setShowHistoryLimitDialog] = useState(false);
  const handleAccept = useCallback((message) => {
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "invite_to_game_accept";
    generalMessage.game.gameId = message.game.gameId;
    generalMessage.gameInfo = message.gameInfo;
    sendMessage(generalMessage);
  }, []);

  const handleReject = useCallback((message) => {
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "invite_to_game_reject";
    generalMessage.game.gameId = message.game.gameId;
    sendMessage(generalMessage);
  }, []);

  const generateGameDetailsHtml = useCallback(
    (data) => {
      const storedLevels = JSON.parse(localStorage.getItem("levels"));
      let gameLevel = storedLevels[data?.gameInfo?.gameLevelId];
      let time = data.gameInfo.secondsBasedOnGameTimeType;

      let timeLimit = "";
      if (time === 1) {
        timeLimit = "MINUTES_PER_MOVE_1";
      } else if (time === 3) {
        timeLimit = "MINUTES_PER_GAME_3";
      } else if (time === 5) {
        timeLimit = "МINUTES_PER_GAME_5";
      } else if (time === 30) {
        timeLimit = "MINUTES_PER_GAME_30";
      } else if (time === -1) {
        timeLimit = "NO_LIMIT";
      }

      return `
      <div style="text-align: left; line-height: 1.6;">
        <p><strong>${t("Initiator")}:</strong> ${data?.user?.name}</p>
        <p><strong>${t("Game Type")}:</strong> ${t(data?.gameInfo?.gameTeamType)}</p>
        <p><strong>${t("Time Limit")}:</strong> ${t(timeLimit)}</p>
        
        <p><strong>${t("Win Coins")}:</strong> ${gameLevel?.winCoins}</p>
        <p><strong>${t("Loss Coins")}:</strong> ${gameLevel?.loseCoins}</p>
        <p><strong>${t("Draw Coins")}:</strong> ${gameLevel?.drawCoins}</p>
       
      </div>
    `;
    },
    [t]
  );

  const showCountdownSwal = useCallback(
    (title, message, timeoutSeconds, htmlContent) => {
      let timerInterval;
      let timeLeft = timeoutSeconds;

      Swal.fire({
        title: title,
        html: `
        <div style="text-align: center;">
          ${htmlContent}
          <div style="margin-top: 15px;">
            <div id="countdown-circle" style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background-color: red;
              color: white;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 1rem;
              margin-bottom: 5px;
            "></div>
            <div style="color: red;">${t("seconds remaining")}</div>
          </div>
        </div>
      `,
        timer: timeoutSeconds * 1000,
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "info",
        showDenyButton: true,
        confirmButtonText: t("Accept"),
        denyButtonText: t("Decline"),
        didOpen: () => {
          const timerEl =
            Swal.getHtmlContainer().querySelector("#countdown-circle");
          timerInterval = setInterval(() => {
            if (timeLeft > 0) timeLeft--;
            if (timerEl) timerEl.textContent = timeLeft;
          }, 1000);
        },
        willClose: () => {
          clearInterval(timerInterval);
        },
      }).then((result) => {
        if (result.dismiss === Swal.DismissReason.timer || result.isDenied) {
          handleReject(message);
        } else if (result.isConfirmed) {
          handleAccept(message);
        }
      });
    },
    [t, handleAccept, handleReject]
  );

  // Memoize handleWebSocketEvent to prevent unnecessary re-renders
  const handleWebSocketEvent = useCallback(
    (event) => {
      if (event.type === "message") {
        if (event.message.messageType === "invite_to_game") {
          let timeLeft = 300;
          let title = t("You have been invited to a game");
          let html = `
          <div style="text-align: left;">
            
            <h2 style="margin-bottom: 15px; color: #4a707d; text-align: center;">${event.message.team?.name || t("N/A")}</h2>
            ${generateGameDetailsHtml(event.message)}
            
            </div>
          </div>`;
          showCountdownSwal(title, event.message, timeLeft, html);
        } else if (event.message.messageType === "reward") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                title: t("Daily Reward!"),
                text:
                  t("You just received") +
                  " " +
                  event.message.message +
                  " " +
                  t(
                    "free coins! Come back every day to claim your daily reward"
                  ),
                icon: "success",
                confirmButtonText: t("Awesome!"),
              });
            }
          });
        } else if (event.message.messageType === "game_history_limit_reached") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                title: t("You have reached the limit"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t("Upgrade your subscription"),
                text: t(
                  "You’ve reached your game history viewing limit. To view more past games, please upgrade your subscription"
                ),
                cancelButtonText: t("Cancel"),
              }).then((result) => {
                if (result.isConfirmed) {
                  setShowHistoryLimitDialog(true); // ✅ open your dialog
                }
              });
            }
          });
        } else if (event.message.messageType === "team_limit_reached") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                title: t("You have reached the limit"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t("Upgrade your subscription"),
                text: t(
                  "To create additional teams, please consider upgrading your subscription"
                ),
                cancelButtonText: t("Cancel"),
              }).then((result) => {
                if (result.isConfirmed) {
                  setShowHistoryLimitDialog(true); // ✅ open your dialog
                }
              });
            }
          });
        } else if (event.message.messageType === "game_hints_limit_reached") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                title: t("You have reached the limit"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t("Upgrade your subscription"),
                text: t("game_hints_limit_reached"),
                cancelButtonText: t("Cancel"),
              }).then((result) => {
                if (result.isConfirmed) {
                  setShowHistoryLimitDialog(true); // ✅ open your dialog
                }
              });
            }
          });
        } else if (event.message.messageType === "no_viewer") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t("Upgrade your subscription"),
                text: t("no_viewer_access"),
                cancelButtonText: t("Cancel"),
              }).then((result) => {
                if (result.isConfirmed) {
                  setShowHistoryLimitDialog(true); // ✅ open your dialog
                }
              });
            }
          });
        } else if (event.message.messageType === "transfer_failed") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              Swal.fire({
                title: t("Coin transfer failed"),
                icon: "error",
                showConfirmButton: false, // Hide the OK button
                showCancelButton: true,
                cancelButtonColor: "#d33",
                cancelButtonText: t("Cancel"),
              });
            }
          });
        } else if (event.message.messageType === "transfer_coins_ok") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              Swal.fire({
                title: t("Done! The coin transfer is complete"),
                icon: "info",
              });
            }
          });
        } else if (event.message.messageType === "coins_receiced") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              Swal.fire({
                title: t("You got coins from your friend!"),
                icon: "info",
              });
            }
          });
        } else if (event.message.messageType === "settings_updated") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              Swal.fire({
                title: t("Your settings have been updated successfully"),
                icon: "info",
              });
            }
          });
        } else if (
          event.message.messageType === "not_allowedToBeAddedToFavorites"
        ) {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                icon: "warning",
                showCancelButton: true,
                showConfirmButton: false, // Hide the OK button
                cancelButtonColor: "#d33",

                text: t(
                  "You can't add this player to your favorites — they have disabled this option"
                ),
                cancelButtonText: t("Cancel"),
              });
            }
          });
        } else if (event.message.messageType === "not_allowedToBeAddedToGame") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                icon: "warning",
                showCancelButton: true,
                showConfirmButton: false, // Hide the OK button
                cancelButtonColor: "#d33",

                text: t(
                  "You can't add this player to the game — they’ve chosen to disable invitations from others"
                ),
                cancelButtonText: t("Cancel"),
              });
            }
          });
        } else if (event.message.messageType === "general_error") {
          setDialogData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              // ✅ Show translated SweetAlert
              Swal.fire({
                title: t("We’re sorry, something went wrong"),
                icon: "error",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t("Contact support"),
                text: t(
                  "Please try again, or contact our support team if the issue persists"
                ),
                cancelButtonText: t("Cancel"),
              }).then((result) => {
                if (result.isConfirmed) {
                  navigate("/contactus");
                } else if (
                  result.isDismissed ||
                  result.dismiss === Swal.DismissReason.cancel
                ) {
                  navigate("/main");
                }
              });
            }
          });
        } else if (event.message.messageType === "uncompleted_game") {
          setUncompletedGameWS((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(event.message)) {
              return event.message;
            }
            return prev;
          });

          // Avoid unnecessary writes to localStorage
          const existingGame = localStorage.getItem("uncompleted_game");
          const newGame = JSON.stringify(event.message.gameInfo);
          if (existingGame !== newGame) {
            localStorage.setItem("uncompleted_game", newGame);
          }
        } else if (event.message.messageType === "invite_to_game_canceled") {
          Swal.fire({
            title: t("Team Play"),
            text: t(
              "One or more players have declined the team game invitation"
            ),
            icon: "error",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        } else if (event.message.messageType === "user_not_found") {
          Swal.fire({
            title: t("Team Play"),
            text: t("One or more players assigned to the team were not found"),
            icon: "error",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        } else if (event.message.messageType === "user_offline") {
          Swal.fire({
            title: t("Team Play"),
            text:
              t("The user is currently offline") + ": " + event.message.message,
            icon: "error",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        } else if (event.message.messageType === "invalid_team_formation") {
          Swal.fire({
            title: t("Team Play"),
            text: t(
              "The team formation is not compatible with the selected game"
            ),
            icon: "error",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        } else if (event.message.messageType === "team_invite_sent") {
          Swal.fire({
            title: t("Team Play"),
            text: t(
              "The team game invitation has been sent. Waiting for players to respond"
            ),
            icon: "success",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        } else if (event.message.messageType === "friend_added") {
          Swal.fire({
            title: t("Friend added!"),
            text: t("You have successfully added a new friend"),
            icon: "success",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        } else if (event.message.messageType === "invalid_code") {
          Swal.fire({
            title: t("Invalid code"),
            text: t(
              "We couldn’t locate your friend using that code. Double-check and try again"
            ),
            icon: "error",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        } else if (event.message.messageType === "you_already_friends") {
          Swal.fire({
            title: t("Already friends"),
            text: t("Looks like this user is already saved in your favorites"),
            icon: "error",
            confirmButtonText: t("OK"),
            draggable: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        }
      }
    },
    [generateGameDetailsHtml, navigate, showCountdownSwal, t]
  );

  useEffect(() => {
    addListener(handleWebSocketEvent);
    return () => {
      removeListener(handleWebSocketEvent);
    };
  }, [handleWebSocketEvent]); // Dependency array ensures `handleWebSocketEvent` doesn't change

  return (
    <WebSocketContext.Provider
      value={{
        dialogData,
        setDialogData,
        showHistoryLimitDialog,
        setShowHistoryLimitDialog,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
